import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { MyKnowledgeService } from '../../services/my-knowledge.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommentService } from '../../services/comment.service';
import { ApiConfigService } from '../../services/api-config.service';
import { CountrySelectionService } from '../../services/country-selection.service';
import { forkJoin } from 'rxjs';

interface Comment {
  userName: string;
  commentText: string;
  dateTime: Date;
}

@Component({
  selector: 'app-my-knowledge-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './my-knowledge-details.component.html',
  styleUrls: ['./my-knowledge-details.component.css']
})
export class MyKnowledgeDetailsComponent implements OnInit {
  item: any | undefined;
  commentForm: FormGroup;
  commentSubmitted: boolean = false;
  copySuccess: boolean = false;
  previousComments: Comment[] = [];
  relatedArticles: any[] = [];
  apiData: any;
  emailData: any;
  baseUrl: string;
  selectedCountry: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private fb: FormBuilder,
    private commentService: CommentService,
    private myKnowledgeService: MyKnowledgeService,
    private apiConfigService: ApiConfigService,
    private countrySelectionService: CountrySelectionService
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.item = navigation?.extras.state?.['item'];
    this.baseUrl = this.apiConfigService.getApiUrl('');

    this.commentForm = this.fb.group({
      comment: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(200)]]
    });
  }

  ngOnInit(): void {
    this.countrySelectionService.selectedCountry$.subscribe(country => {
      this.selectedCountry = country;
      this.route.paramMap.subscribe(params => {
        const id = params.get('id');
        if (id) {
          this.myKnowledgeService.getContentById(id, this.selectedCountry.id).subscribe(item => {
            if (item) {
              this.item = item;
              this.loadComments();
              this.loadRelatedArticles();
            } else {
              console.error('Item not found');
            }
          });
        } else if (this.item) {
          this.loadComments();
          this.loadRelatedArticles();
        }
      });
    });
  }

  loadComments(): void {
    if (this.item && this.item.nid) {
      this.commentService.getComments(this.item.nid).subscribe((comments: any) => {
        this.previousComments = comments.map((comment: any) => ({
          userName: comment.subject,
          commentText: this.decode(comment.comment_body),
          dateTime: new Date(comment.created * 1000)
        }));
      });
    }
  }

  loadRelatedArticles(): void {
    if (this.item && this.item.field_recommended_articles_export) {
      const relatedIds = this.item.field_recommended_articles_export;
      if (relatedIds.length > 0) {
        const observables = relatedIds.map((id: string) => 
          this.myKnowledgeService.getContentById(id, this.selectedCountry.id)
        );
        forkJoin(observables).subscribe((articles: unknown) => {
          if (Array.isArray(articles)) {
            this.relatedArticles = articles.filter((article: any) => article);
          }
        });
      }
    }
  }

  decode(text: string): string {
    const parser = new DOMParser();
    const decodedString = parser.parseFromString(`<!doctype html><body>${text}`, 'text/html').body.textContent;
    return decodedString || '';
  }

  goBack(): void {
    this.location.back();
  }

  copyUrlToClipboard(): void {
    const url = `${window.location.origin}/#/my-knowledge-details/${this.item.nid}`;
    navigator.clipboard.writeText(url).then(() => {
      this.copySuccess = true;
      setTimeout(() => {
        this.copySuccess = false;
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  }

  navigateToArticle(article: any): void {
    this.router.navigate(['/my-knowledge-details', article.nid], { state: { item: article } });
  }

  submitComment(): void {
    this.commentForm.markAllAsTouched();

    if (this.commentForm.valid && this.item) {
      const commentText = this.commentForm.value.comment;
      const userName = 'Anonymous User';
      const newComment = {
        nid: this.item.nid,
        subject: 'Anonymous',
        body: commentText,
        field_country: {
          value: this.selectedCountry.id
        }
      };

      this.commentService.postComment(newComment).subscribe(response => {
        this.commentSubmitted = true;
        this.commentForm.reset();
        setTimeout(() => {
          this.commentSubmitted = false;
        }, 5000);
      });
    }
  }
}
